import * as React from 'react';
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Avatar, Divider, Typography, Button, ClickAwayListener, Grow, Paper, Popper, MenuItem, ListItemIcon, Icon, MenuList, Stack } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { connect } from 'react-redux'
import { AppState } from '../../reducers'
import { isClient, SessionState } from '../../reducers/auth/session'
import { doLogout } from '../../actions/auth/session'
import { useThemeContext } from '../../theme/ThemeContext'
import { useTranslation } from "react-i18next";
import { PERSON } from '../../models/User';
import { roleToURLPrefix } from '../../App';

interface Props {
  session?: SessionState
}

const UserMenu: React.FC<Props> = ({ session }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { theme } = useThemeContext()
  const { t } = useTranslation(['login', 'person'])

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const profile = (event: Event | React.SyntheticEvent) => {
    handleClose(event)
    session && history.push(`/${session.roles && roleToURLPrefix(session.roles.filter(role => role !== PERSON)[0])}/profile`, { title: t('myInfo', { ns: 'person' }) })
  }

  const people = (event: Event | React.SyntheticEvent) => {
    handleClose(event)
    session && history.push(`/client/people`)
  }

  const logout = (event: Event | React.SyntheticEvent) => {
    handleClose(event)
    dispatch(doLogout(history))
  };


  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Stack direction="row">
      <div>
        <Button sx={{
          textTransform: "none", backgroundColor: theme.palette.background.default,
          "&.Mui-selected": {
            backgroundColor: theme.palette.background.default
          },
          ":hover": {
            backgroundColor: theme.palette.action.hover
          }
        }}
          ref={anchorRef}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          endIcon={<KeyboardArrowDown sx={{ color: "grey" }} />}
        >
          <div style={{ display: "flex", alignItems: 'center', paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2) }}>
            <Avatar>{session?.fullName?.match(/\b(\w)/g)?.join('')}</Avatar>
            <div style={{ justifyContent: 'center', paddingLeft: theme.spacing(2), display: "flex", flexDirection: "column", backgroundColor: 'transparent' }}>
              <Typography variant="h6" fontWeight='bold' color="primary">
                &nbsp;{session?.fullName}&nbsp;
              </Typography>
              <Typography variant="subtitle2" noWrap>
                {session?.email}
              </Typography>
            </div>
          </div>
        </Button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-end"
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom-end' ? 'right top' : 'left top',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                  >
                    <MenuItem onClick={profile}>
                      <ListItemIcon sx={{ minWidth: "35px" }}><Icon sx={{ color: theme.typography.caption.color }}>{'person'}</Icon></ListItemIcon>
                      {t('profile')}
                    </MenuItem>
                    {isClient(session!) &&
                      [<Divider key="divider-people"/>,
                      <MenuItem onClick={people} key="people">
                        <ListItemIcon sx={{ minWidth: "35px" }}><Icon sx={{ color: theme.typography.caption.color }}>{'contacts'}</Icon></ListItemIcon>
                        {t('people')}
                      </MenuItem>]
                    }
                    <Divider />
                    <MenuItem onClick={logout}>
                      <ListItemIcon sx={{ minWidth: "35px" }}><Icon sx={{ color: theme.typography.caption.color }}>{'logout'}</Icon></ListItemIcon>
                      {t('logout')}
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </Stack>
  );
}

const mapStateToProps = (state: AppState) => ({
  session: state.session
})

export default connect(mapStateToProps, null)(UserMenu)
